import { useContext, useMemo, useState } from 'react'
import { shallow } from 'zustand/shallow'
import { fireAlertToast } from '@/design-system/alerts'
import { AxiosContext } from '@/context/Axios'
import { useConnectionAccept, useConnectionAdd, useConnectionCancel, useConnectionDecline } from '@/hooks/api/dashboard'
import useAppStore from '@/store/useAppStore'
import { PersonActionState } from '../components/PersonActionButtons'

export interface ConnectRecipient {
  userID: string
  name: string
  profession?: string
  location?: string
  avatarUrl?: string
}

export type CirclePickerTarget =
  | { mode: 'accept'; connReqID: string; name: string }
  | ({ mode: 'connect' } & ConnectRecipient)

export interface UseConnectionRequestActionsOptions {
  /** Fired after a request is accepted — caller updates its own request list/counts. */
  onAccepted?: (connReqID: string) => void
  /** Fired after a connect request is sent — caller can track it for a "Sent" tab, toasts, etc. */
  onConnected?: (userID: string, name: string) => void
  /** Fired after a request is declined — caller updates its own request list/counts. */
  onDeclined?: (connReqID: string) => void
}

/**
 * Accept / decline / connect / cancel wiring shared by anywhere a person row
 * can show connection actions — ConnectionsScreen's Requests + Sent + Search
 * tabs, and the global search results People tab. Both the circle-picker
 * modal (accept and connect both require picking a circle) and the "which
 * requestID/userID is this row's action currently in flight for" bookkeeping
 * live here so callers only need to render <PersonActionButtons> +
 * <CirclePickerModal>.
 *
 * Sent requests are persisted in the appStore (survives reload/tab close) —
 * there's no bulk "list requests I've sent" endpoint on the backend, so this
 * is the actual ceiling: we track what we ourselves have sent, durably,
 * rather than only for the current tab session. Cancelling a sent request
 * reuses the same connReqUpdate(status 2) call `useConnectionDecline` makes,
 * just against the sender's own request id — backfilled via basicInfoLoad's
 * `reqSentConnReqID` on first cancel, since connReqAdd doesn't return the id.
 */
export function useConnectionRequestActions(options: UseConnectionRequestActionsOptions = {}) {
  const axios = useContext(AxiosContext)
  const acceptConnection = useConnectionAccept()
  const addConnection = useConnectionAdd()
  const declineConnection = useConnectionDecline()
  const cancelConnection = useConnectionCancel()

  const [sentConnectionRequestsRaw, addSentConnectionRequest, removeSentConnectionRequest, setSentConnectionRequestID] =
    useAppStore(
      (state) => [
        state.user.sentConnectionRequests,
        state.addSentConnectionRequest,
        state.removeSentConnectionRequest,
        state.setSentConnectionRequestID,
      ],
      shallow,
    )
  // Defensive — older persisted sessions from before this field existed can
  // still rehydrate it as undefined (see useAppStore's merge for the real
  // fix); never let a stale localStorage snapshot crash this hook.
  const sentConnectionRequests = sentConnectionRequestsRaw || []

  const [circlePicker, setCirclePicker] = useState<CirclePickerTarget | null>(null)
  const [decliningID, setDecliningID] = useState<string | null>(null)
  const [cancellingID, setCancellingID] = useState<string | null>(null)

  const sentIDs = useMemo(() => new Set(sentConnectionRequests.map((r) => r.userID)), [sentConnectionRequests])

  const openAccept = (connReqID: string, name: string) => setCirclePicker({ mode: 'accept', connReqID, name })
  const openConnect = (recipient: ConnectRecipient) => setCirclePicker({ mode: 'connect', ...recipient })
  const closeCirclePicker = () => setCirclePicker(null)

  const onSubmitCircle = async (circleID: string) => {
    if (!circlePicker) return
    try {
      if (circlePicker.mode === 'accept') {
        await acceptConnection.mutateAsync({ connReqID: circlePicker.connReqID, cliques: [circleID] })
        fireAlertToast('connection.accepted')
        options.onAccepted?.(circlePicker.connReqID)
      } else {
        await addConnection.mutateAsync({ connID: circlePicker.userID, cliques: [circleID] })
        fireAlertToast('connection.requestSent')
        addSentConnectionRequest({
          userID: circlePicker.userID,
          name: circlePicker.name,
          profession: circlePicker.profession,
          location: circlePicker.location,
          avatarUrl: circlePicker.avatarUrl,
          connReqID: null,
          sentAt: new Date().toISOString(),
        })
        options.onConnected?.(circlePicker.userID, circlePicker.name)
      }
      setCirclePicker(null)
    } catch {
      fireAlertToast(circlePicker.mode === 'accept' ? 'connection.acceptFailed' : 'connection.requestFailed')
    }
  }

  const onDecline = async (connReqID: string) => {
    setDecliningID(connReqID)
    try {
      await declineConnection.mutateAsync(connReqID)
      fireAlertToast('connection.declined')
      options.onDeclined?.(connReqID)
    } catch {
      fireAlertToast('connection.declineFailed')
    } finally {
      setDecliningID(null)
    }
  }

  /**
   * Cancels a request the current user sent. Works even for rows we didn't
   * persist ourselves (e.g. sent from another device/session, surfaced via
   * a search/recommendation row's own `reqSent` flag) — falls back to
   * basicInfoLoad's `reqSentConnReqID` whenever we don't already have the id.
   */
  const onCancelSent = async (userID: string) => {
    const pending = sentConnectionRequests.find((r) => r.userID === userID)
    setCancellingID(userID)
    try {
      let connReqID = pending?.connReqID ?? null
      if (!connReqID) {
        const response = await axios.api.post('ajax/users', {
          requestType: 'basicInfoLoad',
          format: 'json',
          userID,
        })
        const fetchedID = response.data?.users?.reqSentConnReqID
        connReqID = fetchedID ? String(fetchedID) : null
        if (connReqID) setSentConnectionRequestID(userID, connReqID)
      }
      if (!connReqID) {
        // Backend no longer has a pending request for this user (already
        // resolved on the other side) — just drop it from our own list.
        removeSentConnectionRequest(userID)
        return
      }
      await cancelConnection.mutateAsync(connReqID)
      removeSentConnectionRequest(userID)
      fireAlertToast('connection.cancelled')
    } catch {
      fireAlertToast('connection.cancelFailed')
    } finally {
      setCancellingID(null)
    }
  }

  /** Resolves a person's raw connection fields into the state PersonActionButtons expects. */
  const getActionState = (opts: { isConnection?: boolean; userID: string; hasReceivedRequest?: boolean }): PersonActionState => {
    if (opts.isConnection) return 'connected'
    if (opts.hasReceivedRequest) return 'received'
    if (sentIDs.has(opts.userID)) return 'sent'
    return 'none'
  }

  return {
    circlePicker,
    openAccept,
    openConnect,
    closeCirclePicker,
    onSubmitCircle,
    onDecline,
    onCancelSent,
    decliningID,
    cancellingID,
    sentIDs,
    sentConnectionRequests,
    getActionState,
    accepting: acceptConnection.isLoading,
    connecting: addConnection.isLoading,
    declining: declineConnection.isLoading,
    cancelling: cancelConnection.isLoading,
  }
}
